import React, { Fragment, ReactNode, useEffect, useRef } from 'react';
import './WizardDialog.less';
import { Dialog as HeadlessDialog, Transition } from '@headlessui/react';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import Icon from '../Icon/Icon';
import useEscape from '../../../helper/Hooks';

export interface WizardDialogProps {
    title: string;
    steps: string[];
    currentStep: number;
    visible: boolean;
    onClose: () => void;
    // Fires once the close transition has fully finished (content unmounted from
    // view) — use this for state resets so the user never sees the reset content
    // flash during the closing animation.
    onClosed?: () => void;
    footer: ReactNode;
    children: ReactNode;
    size?: 'default' | 'medium' | 'wide';
    bodyClassName?: string;
}

const WizardDialog = ({
    title,
    steps,
    currentStep,
    visible,
    onClose,
    onClosed,
    footer,
    children,
    size = 'default',
    bodyClassName,
}: WizardDialogProps) => {
    const { t } = useTranslation();
    useEscape(() => onClose());

    const titleId = useRef(`wizard-dialog-title-${Math.random().toString(36).slice(2)}`).current;

    useEffect(() => {
        return () => {
            setTimeout(() => {
                const portal = document.getElementById('headlessui-portal-root');
                if (!portal) {
                    document.documentElement.removeAttribute('style');
                }
            }, 500);
        };
    }, [visible]);

    const focusRef = useRef<HTMLButtonElement>(null);

    return (
        <Transition as={Fragment} show={visible} afterLeave={onClosed}>
            <HeadlessDialog
                static={true}
                initialFocus={focusRef}
                open={visible}
                className="dialog"
                onClose={() => {}}
                aria-labelledby={titleId}
                aria-modal={true}
            >
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-500"
                    // Opacity only — no scale/translate: under Tailwind 3 such a class leaves a lasting
                    // transform on the wrapper, which re-anchors the position:fixed content to the
                    // wrapper instead of the viewport (shifted camera view on re-open, qa#134).
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="dialog--wrapper">
                        <HeadlessDialog.Overlay className="dialog--overlay" />
                        <div
                            className={classNames('wizard-dialog--content', {
                                'wizard-dialog--content-medium': size === 'medium',
                                'wizard-dialog--content-wide': size === 'wide',
                            })}
                        >
                            {/* Sticky header */}
                            <div className="wizard-dialog--header">
                                <HeadlessDialog.Title id={titleId} className="wizard-dialog--title">
                                    {title}
                                </HeadlessDialog.Title>
                                <button
                                    ref={focusRef}
                                    aria-label={t('common.close')}
                                    className="wizard-dialog--close"
                                    onClick={onClose}
                                >
                                    <Icon aria={{ 'aria-hidden': true }} path={Icon.Path.mdiClose} className="w-7 text-gray-400 hover:text-gray-600 transition" />
                                </button>
                            </div>

                            {/* Sticky step indicator */}
                            <div className="wizard-dialog--steps">
                                <ol className="wizard-dialog--step-list">
                                    {steps.map((label, index) => {
                                        const stepNumber = index + 1;
                                        const isDone = index < currentStep;
                                        const isActive = index === currentStep;
                                        const isConnectorDone = index > 0 && index <= currentStep;

                                        return (
                                            <Fragment key={label}>
                                                {index > 0 && (
                                                    <li
                                                        role="presentation"
                                                        className={classNames('wizard-dialog--connector', {
                                                            'wizard-dialog--connector-done': isConnectorDone,
                                                            'wizard-dialog--connector-accent': isConnectorDone,
                                                        })}
                                                    />
                                                )}
                                                <li
                                                    aria-current={isActive ? 'step' : undefined}
                                                    className="wizard-dialog--step"
                                                >
                                                    <span
                                                        className={classNames('wizard-dialog--step-circle', {
                                                            'wizard-dialog--step-circle-done': isDone,
                                                            'wizard-dialog--step-circle-accent': isDone,
                                                            'wizard-dialog--step-circle-active': isActive,
                                                            'wizard-dialog--step-circle-primary': isActive,
                                                            'wizard-dialog--step-circle-pending': !isDone && !isActive,
                                                        })}
                                                        aria-hidden={true}
                                                    >
                                                        {isDone ? (
                                                            <Icon path={Icon.Path.mdiCheck} className="w-3.5" />
                                                        ) : (
                                                            stepNumber
                                                        )}
                                                    </span>
                                                    <span
                                                        className={classNames('wizard-dialog--step-label', {
                                                            'wizard-dialog--step-label-done': isDone,
                                                            'wizard-dialog--step-label-accent': isDone,
                                                            'wizard-dialog--step-label-active': isActive,
                                                            'wizard-dialog--step-label-primary': isActive,
                                                            'wizard-dialog--step-label-pending': !isDone && !isActive,
                                                        })}
                                                    >
                                                        {label}
                                                    </span>
                                                </li>
                                            </Fragment>
                                        );
                                    })}
                                </ol>
                            </div>

                            {/* Scrollable content */}
                            <div className={classNames('wizard-dialog--body', bodyClassName)}>
                                {children}
                            </div>

                            {/* Sticky footer */}
                            <div className="wizard-dialog--footer">
                                {footer}
                            </div>
                        </div>
                    </div>
                </Transition.Child>
            </HeadlessDialog>
        </Transition>
    );
};

export default WizardDialog;
